import React, { useMemo } from "react";
import { connect } from "react-redux";
import DataSource from "devextreme/data/data_source";
import { createStore } from 'devextreme-aspnet-data-nojquery';
import {
    Column,
    DataGrid,
    FilterRow,
    Sorting,
    Paging,
    Pager,
    Export,
    ColumnChooser,
    ColumnChooserSearch,
} from "devextreme-react/data-grid";
import apiRoutes from "../../../../assets/apiRoutes.json";
import { EMPTY_GUID, GRID_PAGE_SIZES, CURRENCY_FORMAT } from '../constants';
import SelectCustomerPlaceholder from './SelectCustomerPlaceholder';

// Legacy ARD0009's Cash Receipts tab (grdCashReceipts over uARM0009.dfm's
// qryARCashReceiptHeader) shows Reference, Status, the customer's Identity ID, the check
// number of the receipt's first check-type payment and the summed cash payment amount.
//
// ar/CashReceipt/GetCustomerCashReceipts already returns all of that for one customer -
// including the descriptive StatusDescription from ABFLAGFIELDS, CheckNumber and Amount
// (the sum of ARCASHRECEIPTSPAYMENTS.AMOUNT) - and covers Recordtype R and Z, matching the
// legacy query's own record-type filter. Reused here instead of a second customer-scoped
// cash-receipts endpoint. It is also the endpoint OM's Query Orders By Customer uses.
function CashReceiptsTab(props) {
    const accessToken = props?.oidc?.user?.access_token;
    const hasCustomer = !!props.customerId && props.customerId !== EMPTY_GUID;

    const dataSource = useMemo(() => {
        if (!hasCustomer) return null;
        const source = new DataSource({
            store: createStore({
                key: 'RowId',
                loadParams: { CustomerRowId: props.customerId },
                loadUrl: `${window._env_.REACT_APP_APIS}/${apiRoutes.AR.GetCustomerCashReceipts}`,
                onBeforeSend: (method, ajaxOptions) => {
                    ajaxOptions.headers = { Authorization: `Bearer ${accessToken}`, access_token: accessToken };
                },
            }),
        });
        source.sort([{ selector: 'DateReceived', desc: true }]);
        return source;
    }, [accessToken, hasCustomer, props.customerId]);

    if (!hasCustomer) {
        return <SelectCustomerPlaceholder content="cash receipts" />;
    }

    return (
        <DataGrid
            dataSource={dataSource}
            showBorders={true}
            allowColumnReordering={true}
            allowColumnResizing={true}
            hoverStateEnabled={true}
            remoteOperations={true}
            searchEnabled={true}
            columnAutoWidth={true}
        >
            <ColumnChooser enabled={true} title="Choose Column" width={290} height={290} sortOrder="asc">
                <ColumnChooserSearch enabled={true} editorOptions={{ placeholder: 'Search column' }} />
            </ColumnChooser>
            <Export enabled={true} />
            <Sorting visible={true} mode="multiple" />
            <Paging defaultPageSize={10} />
            <Pager visible={true} showPageSizeSelector={true} allowedPageSizes={GRID_PAGE_SIZES} showNavigationButtons={true} />
            <FilterRow visible={true} />

            {/* Column set mirrors grdCashReceipts. */}
            <Column dataField="RecordNumber" caption="Record #" dataType="string" />
            <Column dataField="Reference" caption="Reference" dataType="string" />
            <Column dataField="StatusDescription" caption="Status" dataType="string" />
            <Column dataField="CustomerCode" caption="Customer" dataType="string" />
            <Column dataField="CheckNumber" caption="Check #" dataType="string" />
            <Column dataField="Amount" caption="Cash Payment Amount" dataType="number" format={CURRENCY_FORMAT} />

            {/* Column chooser (hidden by default) */}
            <Column dataField="DateReceived" caption="Date Received" dataType="datetime" visible={false} />
            <Column dataField="Description" caption="Description" dataType="string" visible={false} />
            <Column dataField="ControlAmount" caption="Control Amount" dataType="number" format={CURRENCY_FORMAT} visible={false} />
            <Column dataField="CommitDate" caption="Commit Date" dataType="datetime" visible={false} />
            <Column dataField="FiscalPeriod" caption="Fiscal Period" dataType="number" visible={false} />
            <Column dataField="FiscalYear" caption="Fiscal Year" dataType="number" visible={false} />
            <Column dataField="Comment" caption="Comment" dataType="string" visible={false} />

            <Column dataField="RowId" caption="Row Id" visible={false} showInColumnChooser={false} />
            <Column dataField="StatusFlag" caption="Status Flag" visible={false} showInColumnChooser={false} />
            <Column dataField="RCustomer" caption="Customer Id" visible={false} showInColumnChooser={false} />
        </DataGrid>
    );
}

function mapStateToProps(state) {
    return {
        oidc: state.oidc
    };
}

export default connect(mapStateToProps)(CashReceiptsTab);
